import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";

import collection from "./img/carres/collection.jpeg";
import angoisse    from "./img/carres/angoisse.jpeg";
import cigarette   from "./img/carres/cigarette.jpeg";
import diagnostic  from "./img/carres/diagnostic.jpeg";
import plafonnement from "./img/carres/plafonnement.jpeg";
import psychiatrie from "./img/carres/psychiatrie.jpeg";
import psychopathe from "./img/carres/psychopathe.jpeg";
import suicide     from "./img/carres/suicide.jpeg";
import temporalite from "./img/carres/temporalite.jpeg";
import traitement  from "./img/carres/traitement.jpeg";

export interface Outil {
  id: number;
  nom: string;
  image: string;
  description: string;
  prix: number; // en centimes
}

export interface PanierElement {
  idOutil: number;
  nombre: number;
}

export const FRAIS_LIVRAISON = 6; // euros
export const FRAIS_LIVRAISON_CENTIMES = 600;

export const outilListe: Outil[] = [
  { id: 0,  nom: "La collection",   image: collection,   description: "9 livrets format A5",  prix: 4500 },
  { id: 1,  nom: "L'angoisse",      image: angoisse,     description: "Livret format A5",      prix: 500  },
  { id: 2,  nom: "La temporalité",  image: temporalite,  description: "Livret format A5",      prix: 500  },
  { id: 3,  nom: "La cigarette",    image: cigarette,    description: "Livret format A5",      prix: 500  },
  { id: 4,  nom: "Le diagnostic",   image: diagnostic,   description: "Livret format A5",      prix: 500  },
  { id: 5,  nom: "Le plafonnement", image: plafonnement, description: "Livret format A5",      prix: 500  },
  { id: 6,  nom: "La psychiatrie",  image: psychiatrie,  description: "Livret format A5",      prix: 500  },
  { id: 7,  nom: "Le psychopathe",  image: psychopathe,  description: "Livret format A5",      prix: 500  },
  { id: 8,  nom: "Le traitement",   image: traitement,   description: "Livret format A5",      prix: 500  },
  { id: 9,  nom: "Le suicide",      image: suicide,      description: "Livret format A5",      prix: 500  },
];

/* ─── Helpers ─────────────────────────────────────── */

export const getOutil = (id: number): Outil | undefined =>
  outilListe.find((o) => o.id === id);

export const calcTotal = (panier: PanierElement[]): number =>
  panier.reduce((acc, item) => {
    const o = getOutil(item.idOutil);
    return o ? acc + o.prix * item.nombre : acc;
  }, 0);

export const calcTotalAvecLivraison = (panier: PanierElement[]): number =>
  calcTotal(panier) + FRAIS_LIVRAISON_CENTIMES;

/* ─── Context ─────────────────────────────────────── */

type PanierContextType = {
  panier: PanierElement[];
  setPanier: React.Dispatch<React.SetStateAction<PanierElement[]>>;
  ajouterOutil: (id: number) => void;
  diminuerOutil: (id: number) => void;
  supprimerOutil: (id: number) => void;
  viderPanier: () => void;
  getQuantite: (id: number) => number;
  total: number;
  totalAvecLivraison: number;
};

const PanierContext = createContext<PanierContextType | undefined>(undefined);

export const PanierProvider = ({ children }: { children: ReactNode }) => {
  const [panier, setPanier] = useState<PanierElement[]>(() => {
    try {
      const stored = localStorage.getItem("panier");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("panier", JSON.stringify(panier));
  }, [panier]);

  const ajouterOutil = (id: number) => {
    setPanier((prev) => {
      const existant = prev.find((el) => el.idOutil === id);
      if (existant) {
        return prev.map((el) =>
          el.idOutil === id ? { ...el, nombre: el.nombre + 1 } : el
        );
      }
      return [...prev, { idOutil: id, nombre: 1 }];
    });
  };

  const diminuerOutil = (id: number) => {
    setPanier((prev) =>
      prev
        .map((el) => (el.idOutil === id ? { ...el, nombre: el.nombre - 1 } : el))
        .filter((el) => el.nombre > 0)
    );
  };

  const supprimerOutil = (id: number) => {
    setPanier((prev) => prev.filter((el) => el.idOutil !== id));
  };

  const viderPanier = () => setPanier([]);

  const getQuantite = (id: number): number =>
    panier.find((p) => p.idOutil === id)?.nombre ?? 0;

  return (
    <PanierContext.Provider
      value={{
        panier,
        setPanier,
        ajouterOutil,
        diminuerOutil,
        supprimerOutil,
        viderPanier,
        getQuantite,
        total: calcTotal(panier),
        totalAvecLivraison: calcTotalAvecLivraison(panier),
      }}
    >
      {children}
    </PanierContext.Provider>
  );
};

export const usePanier = () => {
  const ctx = useContext(PanierContext);
  if (!ctx) throw new Error("usePanier doit être utilisé dans un PanierProvider");
  return ctx;
};
